import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { PublicAppSettings } from '../types';

const DEFAULT: PublicAppSettings = { categorization: { enabled: true } };

let cache: PublicAppSettings | null = null;
let inflight: Promise<PublicAppSettings> | null = null;

async function load(): Promise<PublicAppSettings> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = api.getAppSettings().then(
    (v) => {
      cache = v;
      inflight = null;
      return v;
    },
    () => {
      inflight = null;
      return DEFAULT;
    },
  );
  return inflight;
}

export function invalidateAppSettingsCache(): void {
  cache = null;
}

// MF-69 — read public app settings. Module-level cache shared across
// components; refreshes on window focus so admin flag flips propagate
// without a full page reload.
export function useAppSettings(): PublicAppSettings {
  const [value, setValue] = useState<PublicAppSettings>(cache ?? DEFAULT);

  useEffect(() => {
    let cancelled = false;
    load().then((v) => {
      if (!cancelled) setValue(v);
    });

    const onFocus = () => {
      cache = null;
      load().then((v) => {
        if (!cancelled) setValue(v);
      });
    };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  return value;
}
